import type { NamedSet } from 'zustand/middleware';

import type { ZustandStore } from '../store.type';

type PhoneInfo = { fullPhoneNumber: string; phoneCountryCode: string; phoneNumber: string } | null | undefined;
type StepsData = {
	mfaOtp?: { status: string; secret?: string; qrCode?: string } | null;
	mfaSms?: { status: string; phoneDetails?: PhoneInfo } | null;
	phoneSetup?: { status: string; phoneDetails?: PhoneInfo } | null;
};

type Steps = {
	data: StepsData;
	currentStep: number;
};

type PasswordLoginState = {
	steps: Steps;
	passwordLogin: {
		email: string;
		password: string;
		isFormLoading: boolean;
		error: string | null;
		isMfaLoginRequired: boolean;
		isMfaAppSetupRequired: boolean;
		isMfaSmsSetupRequired: boolean;
		isPhoneSetupRequired: boolean;
		formState: {
			authCode: string;
		};
	};
};

type PasswordLoginActions = {
	setEmail: (email: PasswordLoginState['passwordLogin']['email']) => void;
	setPassword: (password: PasswordLoginState['passwordLogin']['password']) => void;
	setIsFormLoading: (isLoading: PasswordLoginState['passwordLogin']['isFormLoading']) => void;
	setError: (error: PasswordLoginState['passwordLogin']['error']) => void;
	setIsMfaLoginRequired: (isMfaLoginRequired: PasswordLoginState['passwordLogin']['isMfaLoginRequired']) => void;
	setIsMfaAppSetupRequired: (
		isMfaAppSetupRequired: PasswordLoginState['passwordLogin']['isMfaAppSetupRequired'],
	) => void;
	setIsMfaSmsSetupRequired: (
		isMfaSmsSetupRequired: PasswordLoginState['passwordLogin']['isMfaSmsSetupRequired'],
	) => void;
	setIsPhoneSetupRequired: (isPhoneSetupRequired: PasswordLoginState['passwordLogin']['isPhoneSetupRequired']) => void;
	setFormState: (formState: PasswordLoginState['passwordLogin']['formState']) => void;
	resetPasswordLoginState: () => void;
	setStepsData: (data: Partial<StepsData>) => void;
	setCurrentStep: (step: number) => void;
};

export type PasswordLoginSlice = PasswordLoginState & PasswordLoginActions;

const passwordLoginInitialState: PasswordLoginState = {
	steps: { data: {}, currentStep: 0 },
	passwordLogin: {
		email: '',
		password: '',
		isFormLoading: false,
		error: null,
		isMfaLoginRequired: false,
		isMfaAppSetupRequired: false,
		isMfaSmsSetupRequired: false,
		isPhoneSetupRequired: false,
		formState: {
			authCode: '',
		},
	},
};

export function passwordLoginSlice(set: NamedSet<ZustandStore>): PasswordLoginSlice {
	return {
		...passwordLoginInitialState,
		setEmail: (email: PasswordLoginState['passwordLogin']['email']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.email = email;
					return state;
				},
				false,
				'[setEmail]',
			);
		},
		setPassword: (password: PasswordLoginState['passwordLogin']['password']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.password = password;
					return state;
				},
				false,
				'[setPassword]',
			);
		},
		setIsFormLoading: (isLoading: PasswordLoginState['passwordLogin']['isFormLoading']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isFormLoading = isLoading;
					return state;
				},
				false,
				'[setIsFormLoading]',
			);
		},
		setError: (error: PasswordLoginState['passwordLogin']['error']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.error = error;
					return state;
				},
				false,
				'[setError]',
			);
		},
		setIsMfaLoginRequired: (isMfaLoginRequired: PasswordLoginState['passwordLogin']['isMfaLoginRequired']) => {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isMfaLoginRequired = isMfaLoginRequired;
					return state;
				},
				false,
				'[setIsMfaLoginRequired]',
			);
		},
		setIsMfaAppSetupRequired(isMfaAppSetupRequired: PasswordLoginState['passwordLogin']['isMfaAppSetupRequired']) {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isMfaAppSetupRequired = isMfaAppSetupRequired;
					return state;
				},
				false,
				'[setIsMfaAppSetupRequired]',
			);
		},
		setIsMfaSmsSetupRequired(isMfaSmsSetupRequired: PasswordLoginState['passwordLogin']['isMfaSmsSetupRequired']) {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isMfaSmsSetupRequired = isMfaSmsSetupRequired;
					return state;
				},
				false,
				'[setIsMfaSmsSetupRequired]',
			);
		},
		setIsPhoneSetupRequired(isPhoneSetupRequired: PasswordLoginState['passwordLogin']['isPhoneSetupRequired']) {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.isPhoneSetupRequired = isPhoneSetupRequired;
					return state;
				},
				false,
				'[setIsPhoneSetupRequired]',
			);
		},
		setFormState(formState: PasswordLoginState['passwordLogin']['formState']) {
			set(
				(state: PasswordLoginState) => {
					state.passwordLogin.formState = formState;
					return state;
				},
				false,
				'[setFormState]',
			);
		},
		resetPasswordLoginState() {
			set(
				(state: PasswordLoginState) => {
					state = passwordLoginInitialState;
					return state;
				},
				false,
				'[resetPasswordLoginState]',
			);
		},
		setStepsData(data: Partial<StepsData>) {
			set(
				(state: PasswordLoginState) => {
					state.steps.data = { ...state.steps.data, ...data };
					return state;
				},
				false,
				'[setStepsData]',
			);
		},
		setCurrentStep(step: number) {
			set(
				(state: PasswordLoginState) => {
					state.steps.currentStep = step;
					return state;
				},
				false,
				'[setCurrentStep]',
			);
		},
	};
}
